import { FormEvent, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { api, ApiError } from '@/lib/api'
import SiteHeader from '@/components/SiteHeader'
import SiteFooter from '@/components/SiteFooter'
import Seo from '@/components/Seo'
import { Toast, useToast } from '@/components/Toast'

interface ContactPayload {
  name: string
  email: string
  instagram: string
  message: string
}

export default function ContactPage() {
  const { toast, show } = useToast()
  const [form, setForm] = useState<ContactPayload>({
    name: '',
    email: '',
    instagram: '',
    message: '',
  })

  const submit = useMutation({
    mutationFn: (payload: ContactPayload) => api.post('/api/contact', payload),
    onSuccess: () => {
      show("Message sent! We'll be in touch.", 'success')
      setForm({ name: '', email: '', instagram: '', message: '' })
    },
    onError: (err: unknown) => {
      const msg = err instanceof ApiError ? err.message : 'Failed to send. Please try again.'
      show(msg, 'error')
    },
  })

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    submit.mutate({
      name: form.name.trim(),
      email: form.email.trim(),
      instagram: form.instagram.trim(),
      message: form.message.trim(),
    })
  }

  const inputCls =
    'w-full rounded-[10px] border-[1.5px] border-border bg-warm-white px-3.5 py-2.5 font-sans text-[0.9rem] text-ink-primary outline-none transition-colors placeholder:text-ink-tertiary focus:border-brown-light'

  return (
    <div className="flex min-h-screen flex-col bg-warm-white">
      <Seo
        title="Contact Pelvia"
        description="Have a question, feedback, or want to partner with Pelvia? Get in touch with the team behind the first digitized endometriosis clinic."
        path="/contact"
      />
      <SiteHeader />

      <main className="flex flex-1 items-center justify-center px-6 py-12">
        <div className="w-full max-w-[480px]">
          <h1 className="mb-2 font-serif text-[1.6rem] font-medium sm:text-[2rem]">Contact us</h1>
          <p className="mb-8 text-[0.95rem] leading-relaxed text-ink-secondary">
            Have a question, feedback, or just want to say hi? We'd love to hear from you.
          </p>

          <form onSubmit={onSubmit} className="flex flex-col gap-5">
            <div>
              <label htmlFor="name" className="mb-1.5 block text-[0.85rem] font-medium">
                Name
              </label>
              <input
                id="name"
                type="text"
                required
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Your name"
                className={inputCls}
              />
            </div>

            <div>
              <label htmlFor="email" className="mb-1.5 block text-[0.85rem] font-medium">
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="you@example.com"
                className={inputCls}
              />
            </div>

            <div>
              <label htmlFor="instagram" className="mb-1.5 block text-[0.85rem] font-medium">
                Instagram handle{' '}
                <span className="text-[0.8rem] font-normal text-ink-tertiary">(optional)</span>
              </label>
              <input
                id="instagram"
                type="text"
                value={form.instagram}
                onChange={(e) => setForm((f) => ({ ...f, instagram: e.target.value }))}
                placeholder="@yourhandle"
                className={inputCls}
              />
            </div>

            <div>
              <label htmlFor="message" className="mb-1.5 block text-[0.85rem] font-medium">
                Message
              </label>
              <textarea
                id="message"
                required
                rows={5}
                value={form.message}
                onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                placeholder="What's on your mind?"
                className={`${inputCls} min-h-[120px] resize-y`}
              />
            </div>

            <button
              type="submit"
              disabled={submit.isPending}
              className="mt-2 w-full rounded-[10px] bg-brown py-3 font-sans text-[0.95rem] font-semibold text-white transition-[background,transform] hover:bg-brown-dark active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submit.isPending ? 'Sending…' : 'Send message'}
            </button>
          </form>

          <p className="mt-7 text-center text-[0.82rem] leading-relaxed text-ink-tertiary">
            We'll get to your message as soon as possible.
            <br />
            Pelvia.me
          </p>
        </div>
      </main>

      <Toast toast={toast} />
      <SiteFooter />
    </div>
  )
}
